import type { ReactNode } from "react";
import { useMemo } from "react";

import { styled } from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { SWReactCountryIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Card, Link, TitlePlaceholderLoader, ListItemPlaceholderLoader } from "@similarweb/ui-components";
import { Flex, Text, Box } from "@similarweb/ui-components/foundations";

import { swReactRankNumberFilter } from "app/filters/ngFilters";
import { WebsiteAnalysisTrendsBar } from "app/pages/website-analysis/components/WebsiteAnalysisTrendsBar";
import type { IWebsiteAnalysisTrafficRequestParams } from "app/pages/website-analysis/TrafficAndEngagement/types";
import CountryService from "app/services/CountryService";
import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useWebRanksData } from "./useWebRanksData";

const StyledLink = styled(Link)`
    display: block;
    max-width: 100%;
    color: ${colorsPalettes.blue[400]};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const EllipsisSpan = styled.span`
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

interface IRankRowProps {
    label: string;
    value: number;
    trend?: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
    trendKey: string;
    subtitle?: ReactNode;
}

const RankRow = ({ label, value, trend, trendKey, subtitle }: IRankRowProps) => (
    <Flex
        justifyContent="space-between"
        alignItems="center"
        flex="1"
        data-automation="website-rank-row"
    >
        <Flex
            flexDirection="column"
            gap="2px"
            minWidth="0"
            flex="1"
            padding="0 4px 0 0"
        >
            <Text
                fontVariant="body1"
                color={colorsPalettes.carbon[400]}
                data-automation="website-rank-title"
            >
                {label}
            </Text>
            {subtitle}
        </Flex>
        <Flex
            alignItems="center"
            gap="16px"
            flexShrink={0}
            minWidth="136px"
            justifyContent="flex-end"
        >
            <Text
                fontVariant="h5"
                color={colorsPalettes.carbon[400]}
                data-automation="website-rank-value"
            >
                {swReactRankNumberFilter()(value, 0)}
            </Text>
            {trend && trend.length > 1 ? (
                <span data-automation="website-rank-trend">
                    <WebsiteAnalysisTrendsBar
                        data={trend}
                        barsLimit={6}
                        inverted
                        rowName={trendKey}
                    />
                </span>
            ) : null}
        </Flex>
    </Flex>
);

interface IWebsiteRankWidgetProps {
    params: {
        country: number;
        from: string;
        to: string;
        key: string;
        webSource: string;
        isWindow: boolean;
        isWWW: boolean;
    };
    apiController: string;
}

export const WebsiteRankWidget = ({ params, apiController }: IWebsiteRankWidgetProps) => {
    const { translate } = useTranslate();
    const apiParams: IWebsiteAnalysisTrafficRequestParams = useMemo(
        () => ({
            country: params.country.toString(),
            from: params.from,
            to: params.to,
            keys: params.key,
            webSource: params.webSource,
            isWindow: params.isWindow,
            includeSubDomains: !params.isWWW,
            timeGranularity: "Monthly",
        }),
        [params.country, params.from, params.to, params.key, params.webSource, params.isWindow, params.isWWW],
    );

    const { data, isLoading, isError } = useWebRanksData(apiParams, apiController);

    const dataObj = useMemo(() => data?.Data?.[params.key], [data, params.key]);
    const countryMostVisitedWorldWide = dataObj?.Country;
    const isWorldWide = params.country === 999;
    const countryID = isWorldWide ? countryMostVisitedWorldWide : params.country;
    const countryName = useMemo(() => CountryService.getCountryById(countryID)?.text, [countryID]);
    const isUSState = useMemo(() => CountryService.isUSState(params.country), [params.country]);
    const splitCategory = useMemo(() => dataObj?.Category?.split("/") ?? [], [dataObj]);
    const isSubCategory = useMemo(() => splitCategory?.length > 1, [splitCategory]);

    const industryRedirectUrl = useMemo(() => {
        const urlParams = {
            country: countryID,
            duration: "1m",
            category: isSubCategory ? `${splitCategory?.[0]}~${splitCategory?.[1]}` : dataObj?.Category,
        };

        if (proNavigator.current().homeState === "salesIntelligence-home") {
            return null;
        }

        return proNavigator.getStateUrl("digitalsuite_marketresearch_webmarketanalysis_mapping", urlParams);
    }, [countryID, isSubCategory, splitCategory, dataObj]);

    const countryRedirectUrl = useMemo(() => {
        const urlParams = {
            country: countryID,
            duration: "1m",
            category: "All",
        };

        if (proNavigator.current().homeState === "salesIntelligence-home") {
            return null;
        }

        return proNavigator.getStateUrl("digitalsuite_marketresearch_webmarketanalysis_mapping", urlParams);
    }, [countryID]);

    const industryLabelText = isSubCategory ? `.../${splitCategory?.[1]}` : splitCategory?.[0];

    if (isLoading) {
        return (
            <Card data-automation="website-rank-widget">
                <Flex
                    flexDirection="column"
                    padding="16px 24px"
                    gap="16px"
                >
                    <TitlePlaceholderLoader />
                    <ListItemPlaceholderLoader />
                    <ListItemPlaceholderLoader />
                </Flex>
            </Card>
        );
    }

    return (
        <Card
            data-automation="website-rank-widget"
            border={`1px solid ${colorsPalettes.carbon[50]}`}
        >
            {isError ? (
                <Flex
                    justifyContent="center"
                    alignItems="center"
                    flex="1"
                    height="100%"
                >
                    <NoDataLandscape
                        title="global.nodata.notavilable"
                        subtitle={null}
                    />
                </Flex>
            ) : (
                <Flex
                    flexDirection="column"
                    padding="0 24px"
                    flex="1"
                >
                    <RankRow
                        label={translate("wa.ao.ranks.global")}
                        value={dataObj?.GlobalRank.Value}
                        trend={dataObj?.GlobalRank?.Trend}
                        trendKey="GlobalRank"
                    />
                    <Box
                        width="100%"
                        height="1px"
                        background={colorsPalettes.carbon[100]}
                    />

                    <RankRow
                        label={translate("wa.ao.ranks.country")}
                        value={dataObj?.CountryRank?.Value}
                        trend={dataObj?.CountryRank?.Trend}
                        trendKey="CountryRank"
                        subtitle={
                            <Flex
                                alignItems="center"
                                gap="4px"
                                minWidth="0"
                                data-automation="website-rank-country-subtitle"
                            >
                                <Flex
                                    width="12px"
                                    height="12px"
                                    flexShrink={0}
                                    data-automation="website-rank-country-icon"
                                >
                                    <SWReactCountryIcons countryCode={isUSState ? 840 : countryMostVisitedWorldWide} />
                                </Flex>
                                <Flex
                                    minWidth="0"
                                    flex="1"
                                >
                                    {countryRedirectUrl ? (
                                        <StyledLink
                                            data-automation="website-rank-country-link"
                                            label={countryName}
                                            url={countryRedirectUrl}
                                            target={isWorldWide ? "_blank" : undefined}
                                        />
                                    ) : (
                                        <Text
                                            fontVariant="caption"
                                            color={colorsPalettes.carbon[400]}
                                            data-automation="website-rank-country-text"
                                        >
                                            <EllipsisSpan>{countryName}</EllipsisSpan>
                                        </Text>
                                    )}
                                </Flex>
                            </Flex>
                        }
                    />
                    <Box
                        width="100%"
                        height="1px"
                        background={colorsPalettes.carbon[100]}
                    />

                    <RankRow
                        label={translate("wa.ao.ranks.category")}
                        value={dataObj?.CategoryRank.Value}
                        trend={dataObj?.CategoryRank?.Trend}
                        trendKey="CategoryRank"
                        subtitle={
                            <Flex minWidth="0">
                                {industryRedirectUrl ? (
                                    <StyledLink
                                        data-automation="website-rank-category-link"
                                        label={industryLabelText}
                                        url={industryRedirectUrl}
                                    />
                                ) : (
                                    <Text
                                        fontVariant="caption"
                                        color={colorsPalettes.carbon[400]}
                                        data-automation="website-rank-category-text"
                                    >
                                        <EllipsisSpan>{industryLabelText}</EllipsisSpan>
                                    </Text>
                                )}
                            </Flex>
                        }
                    />
                </Flex>
            )}
        </Card>
    );
};
